import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Download, X, Smartphone, Share } from 'lucide-react';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  const isAdmin = window.location.pathname.startsWith('/admin');

  useEffect(() => {
    // Check if already running in standalone mode (installed)
    const checkStandalone = 
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    
    setIsStandalone(checkStandalone);

    // Detect if iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const detectIOS = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(detectIOS);

    // Capture Chrome/Android install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
      if (!checkStandalone && !isDismissed) {
        setIsVisible(true);
      }
    };

    // Check if the prompt was already captured by the early script in index.html
    const earlyPrompt = (window as any).deferredPWAInstallPrompt;
    if (earlyPrompt) {
      setDeferredPrompt(earlyPrompt);
      const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
      if (!checkStandalone && !isDismissed) {
        setIsVisible(true);
      }
    }

    const handleCustomPromptAvailable = (e: any) => {
      const promptEvent = e.detail;
      setDeferredPrompt(promptEvent);
      const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
      if (!checkStandalone && !isDismissed) {
        setIsVisible(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('pwa-prompt-available', handleCustomPromptAvailable as EventListener);

    // If iOS Safari, we can show manual instructions
    if (detectIOS && !checkStandalone) {
      const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
      if (!isDismissed) {
        // Show after a small delay so user is not overwhelmed
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 3000);
        return () => {
          clearTimeout(timer);
          window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
          window.removeEventListener('pwa-prompt-available', handleCustomPromptAvailable as EventListener);
        };
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('pwa-prompt-available', handleCustomPromptAvailable as EventListener);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    // Trigger the native install dialog
    deferredPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] User response to install prompt: ${outcome}`);

    // Clear the deferred prompt variable
    setDeferredPrompt(null);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    sessionStorage.setItem('pwa_prompt_dismissed', 'true');
    setIsVisible(false);
  };

  if (isStandalone || !isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-md pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.95 }}
          className={`bg-gradient-to-r ${isAdmin ? 'from-emerald-800 to-teal-900' : 'from-blue-700 to-indigo-800'} text-white p-4 rounded-2xl shadow-2xl border border-white/10 pointer-events-auto flex items-start gap-3.5 relative overflow-hidden`}
        >
          {/* Subtle Decorative Background Circles */}
          <div className="absolute -right-6 -bottom-6 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none" />
          <div className="absolute -left-6 -top-6 w-20 h-20 bg-emerald-400/10 rounded-full blur-xl pointer-events-none" />

          {/* App Logo Emblem or Icon */}
          <div className="w-11 h-11 rounded-xl bg-white/15 backdrop-blur-md flex items-center justify-center shrink-0 border border-white/10 shadow-inner">
            <Smartphone className={`w-6 h-6 ${isAdmin ? 'text-emerald-200' : 'text-blue-200'} animate-pulse`} />
          </div>

          <div className="flex-1 min-w-0 pr-6">
            <h4 className="font-bold text-sm md:text-base leading-tight">
              {isAdmin ? 'Install Raj Kirana Admin App' : 'Install Raj Kirana Store App'}
            </h4>
            <p className="text-[11px] text-white/90 mt-1 leading-normal font-medium">
              {isAdmin 
                ? 'इन्वेंटरी, आर्डर्स और प्रोडक्ट्स आसानी से मैनेज करें! एडमिन पैनल ऐप इंस्टॉल करें।'
                : 'बिना इंटरनेट के चलाएं और ताज़ा रेट्स देखें! ऐप इंस्टॉल करें और होम स्क्रीन पर रखें।'
              }
            </p>
            
            {/* Action Area */}
            <div className="mt-3 flex items-center gap-2">
              {isIOS ? (
                <div className="flex items-center gap-1.5 text-[11px] bg-white/10 px-2.5 py-1.5 rounded-lg border border-white/10 text-white font-medium">
                  <Share className={`w-3.5 h-3.5 shrink-0 ${isAdmin ? 'text-emerald-200' : 'text-blue-200'}`} />
                  <span>Tap <strong className="font-black text-white">Share</strong> then <strong className="font-black text-white">Add to Home Screen</strong></span>
                </div>
              ) : (
                <button
                  onClick={handleInstallClick}
                  className={`flex items-center gap-1.5 bg-white ${isAdmin ? 'text-emerald-900 hover:bg-emerald-50' : 'text-blue-900 hover:bg-blue-50'} px-3.5 py-1.5 rounded-xl font-bold text-xs shadow-md cursor-pointer transition-all active:scale-95 shrink-0`}
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Install / इंस्टॉल करें</span>
                </button>
              )}
              <button
                onClick={handleDismiss}
                className={`text-[11px] ${isAdmin ? 'text-emerald-200 hover:text-white' : 'text-blue-200 hover:text-white'} px-2 py-1.5 font-semibold cursor-pointer`}
              >
                Later / बाद में
              </button>
            </div>
          </div>

          {/* Absolute Close Button */}
          <button
            onClick={handleDismiss}
            className={`absolute top-2.5 right-2.5 p-1 ${isAdmin ? 'text-emerald-200/80 hover:text-white' : 'text-blue-200/80 hover:text-white'} rounded-full hover:bg-white/10 transition-colors cursor-pointer`}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
